/**
 * Campaign Dashboard — Universal Acquisition Engine
 *
 * Users define their own:
 *  - Target audience (industry, role, qualifiers)
 *  - Email template (subject + body) with {{placeholders}}
 *  - Target countries
 *
 * The engine searches for leads and sends personalized emails automatically.
 */

import { useState, useEffect, useCallback } from 'react';

const ACQUISITION_API = process.env.REACT_APP_ACQUISITION_API || 'http://localhost:5500';

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

interface TargetAudience {
    industry: string;
    targetRole?: string;
    qualifiers?: string;
    exclusions?: string;
    searchGoal?: string;
    idealProfile?: string;
    targetQuantity?: number;
}

interface OutreachMessage {
    subject: string;
    bodyTemplate: string;
}

interface Campaign {
    id: string;
    name: string;
    region: string;
    targetCountries: string[];
    targetAudience: TargetAudience;
    outreachMessage: OutreachMessage;
    status: string;
    leadsDiscovered: number;
    emailsSent: number;
    repliesReceived: number;
    converted: number;
    createdAt: string;
}

interface Lead {
    id: string;
    campaignId: string;
    country: string;
    name: string;
    title: string;
    companyName: string;
    email: string;
    sourceUrl?: string;
    status: string;
    emailsSentCount: number;
}

interface Thread {
    id: string;
    direction: 'inbound' | 'outbound';
    fromEmail: string;
    toEmail: string;
    subject: string;
    bodyText: string;
    receivedAt: string;
}

// ────────────────────────────────────────────────────────────
// API
// ────────────────────────────────────────────────────────────

async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
        const saved = localStorage.getItem('acq_auth');
        if (saved) {
            const auth = JSON.parse(saved);
            if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`;
        }
    } catch { /* ignore parse errors */ }
    const res = await fetch(`${ACQUISITION_API}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new Error(`${method} ${path} → ${res.status}`);
    return res.json() as Promise<T>;
}

// ────────────────────────────────────────────────────────────
// Shared UI Atoms
// ────────────────────────────────────────────────────────────

const colors: Record<string, string> = {
    discovered: '#6366f1', approved: '#22c55e', sent: '#3b82f6',
    replied: '#f59e0b', negotiating: '#8b5cf6', converted: '#10b981',
    rejected: '#ef4444', bounced: '#9ca3af', draft: '#9ca3af',
    running: '#3b82f6', paused: '#f59e0b', completed: '#10b981',
};

function Badge({ label }: { label: string }) {
    return (
        <span style={{
            padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
            background: colors[label] || '#4b5563', color: '#fff',
            textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>{label}</span>
    );
}

const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px', background: '#0a0f1e',
    border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0',
    fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit',
};

const sectionTitle: React.CSSProperties = {
    fontSize: 12, fontWeight: 700, color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
};

// ────────────────────────────────────────────────────────────
// Email Regions & Countries (195 countries → 5 regional mailboxes)
// ────────────────────────────────────────────────────────────

type EmailRegion = 'us' | 'lat' | 'eu' | 'africa' | 'asia';

const REGION_DOMAINS: Record<EmailRegion, string> = {
    us: 'lawhelper.us',
    lat: 'lawhelper.lat',
    eu: 'lawhelper.eu',
    africa: 'lawhelper.africa',
    asia: 'lawhelper.asia',
};

const REGION_DEFAULT_NAMES: Record<EmailRegion, string> = {
    us: 'colin',    // Professional, modern North American
    lat: 'mateo',   // Authentic, widespread Latin American
    eu: 'felix',    // Sophisticated, pan-European
    africa: 'idris',// Recognizable, strong Pan-African
    asia: 'ken',    // Clean, cross-cultural across Asia
};

const REGION_META: Record<EmailRegion, { label: string; icon: string; color: string }> = {
    us: { label: 'North America', icon: '🌎', color: '#3b82f6' },
    lat: { label: 'Latin America', icon: '🌎', color: '#f59e0b' },
    eu: { label: 'Europe', icon: '🌍', color: '#6366f1' },
    africa: { label: 'Africa', icon: '🌍', color: '#22c55e' },
    asia: { label: 'Asia & Oceania', icon: '🌏', color: '#ec4899' },
};

interface Country { code: string; label: string; region: EmailRegion }

const COUNTRIES: Country[] = [
    // ═══ US — North America (2) ═══
    { code: 'US', label: '🇺🇸 USA', region: 'us' },
    { code: 'CA', label: '🇨🇦 Canada', region: 'us' },
    { code: 'MX', label: '🇲🇽 México', region: 'us' },
    // Caribbean → us
    { code: 'CU', label: '�� Cuba', region: 'us' },
    { code: 'HT', label: '�� Haiti', region: 'us' },
    { code: 'DO', label: '�� Dominican Republic', region: 'us' },
    { code: 'JM', label: '�� Jamaica', region: 'us' },
    { code: 'TT', label: '�� Trinidad & Tobago', region: 'us' },
    { code: 'BB', label: '�� Barbados', region: 'us' },
    { code: 'BS', label: '�� Bahamas', region: 'us' },
    { code: 'GD', label: '�� Grenada', region: 'us' },
    { code: 'AG', label: '�� Antigua & Barbuda', region: 'us' },
    { code: 'DM', label: '🇩� Dominica', region: 'us' },
    { code: 'KN', label: '�� St. Kitts & Nevis', region: 'us' },
    { code: 'LC', label: '�� St. Lucia', region: 'us' },
    { code: 'VC', label: '�� St. Vincent', region: 'us' },

    // ═══ LAT — Central & South America (20) ═══
    { code: 'GT', label: '�� Guatemala', region: 'lat' },
    { code: 'BZ', label: '�� Belize', region: 'lat' },
    { code: 'SV', label: '�� El Salvador', region: 'lat' },
    { code: 'HN', label: '�� Honduras', region: 'lat' },
    { code: 'NI', label: '🇳🇮 Nicaragua', region: 'lat' },
    { code: 'CR', label: '🇨🇷 Costa Rica', region: 'lat' },
    { code: 'PA', label: '�� Panamá', region: 'lat' },
    { code: 'CO', label: '🇨🇴 Colombia', region: 'lat' },
    { code: 'VE', label: '🇻🇪 Venezuela', region: 'lat' },
    { code: 'GY', label: '🇬🇾 Guyana', region: 'lat' },
    { code: 'SR', label: '🇸🇷 Suriname', region: 'lat' },
    { code: 'EC', label: '🇪🇨 Ecuador', region: 'lat' },
    { code: 'PE', label: '🇵🇪 Perú', region: 'lat' },
    { code: 'BR', label: '🇧🇷 Brasil', region: 'lat' },
    { code: 'BO', label: '🇧🇴 Bolivia', region: 'lat' },
    { code: 'PY', label: '🇵🇾 Paraguay', region: 'lat' },
    { code: 'UY', label: '🇺🇾 Uruguay', region: 'lat' },
    { code: 'AR', label: '🇦🇷 Argentina', region: 'lat' },
    { code: 'CL', label: '🇨🇱 Chile', region: 'lat' },

    // ═══ EU — Europe, Russia & Caucasus (49) ═══
    { code: 'IS', label: '🇮🇸 Iceland', region: 'eu' },
    { code: 'NO', label: '🇳🇴 Norway', region: 'eu' },
    { code: 'SE', label: '🇸🇪 Sweden', region: 'eu' },
    { code: 'FI', label: '🇫🇮 Finland', region: 'eu' },
    { code: 'DK', label: '🇩🇰 Denmark', region: 'eu' },
    { code: 'GB', label: '🇬🇧 UK', region: 'eu' },
    { code: 'IE', label: '🇮🇪 Ireland', region: 'eu' },
    { code: 'NL', label: '🇳🇱 Netherlands', region: 'eu' },
    { code: 'BE', label: '🇧🇪 Belgium', region: 'eu' },
    { code: 'LU', label: '🇱🇺 Luxembourg', region: 'eu' },
    { code: 'FR', label: '🇫🇷 France', region: 'eu' },
    { code: 'MC', label: '🇲🇨 Monaco', region: 'eu' },
    { code: 'DE', label: '🇩🇪 Germany', region: 'eu' },
    { code: 'AT', label: '🇦🇹 Austria', region: 'eu' },
    { code: 'CH', label: '🇨🇭 Switzerland', region: 'eu' },
    { code: 'LI', label: '🇱🇮 Liechtenstein', region: 'eu' },
    { code: 'ES', label: '🇪🇸 España', region: 'eu' },
    { code: 'PT', label: '🇵🇹 Portugal', region: 'eu' },
    { code: 'AD', label: '🇦🇩 Andorra', region: 'eu' },
    { code: 'IT', label: '🇮🇹 Italy', region: 'eu' },
    { code: 'SM', label: '🇸🇲 San Marino', region: 'eu' },
    { code: 'VA', label: '🇻🇦 Vatican City', region: 'eu' },
    { code: 'MT', label: '🇲🇹 Malta', region: 'eu' },
    { code: 'GR', label: '🇬🇷 Greece', region: 'eu' },
    { code: 'CY', label: '🇨🇾 Cyprus', region: 'eu' },
    { code: 'TR', label: '🇹🇷 Türkiye', region: 'eu' },
    { code: 'PL', label: '🇵🇱 Poland', region: 'eu' },
    { code: 'CZ', label: '🇨🇿 Czechia', region: 'eu' },
    { code: 'SK', label: '🇸🇰 Slovakia', region: 'eu' },
    { code: 'HU', label: '🇭🇺 Hungary', region: 'eu' },
    { code: 'SI', label: '🇸🇮 Slovenia', region: 'eu' },
    { code: 'HR', label: '🇭🇷 Croatia', region: 'eu' },
    { code: 'BA', label: '🇧🇦 Bosnia', region: 'eu' },
    { code: 'RS', label: '🇷🇸 Serbia', region: 'eu' },
    { code: 'ME', label: '🇲🇪 Montenegro', region: 'eu' },
    { code: 'MK', label: '🇲🇰 N. Macedonia', region: 'eu' },
    { code: 'AL', label: '🇦🇱 Albania', region: 'eu' },
    { code: 'BG', label: '🇧🇬 Bulgaria', region: 'eu' },
    { code: 'RO', label: '🇷🇴 Romania', region: 'eu' },
    { code: 'MD', label: '🇲🇩 Moldova', region: 'eu' },
    { code: 'UA', label: '🇺🇦 Ukraine', region: 'eu' },
    { code: 'BY', label: '🇧🇾 Belarus', region: 'eu' },
    { code: 'LT', label: '🇱🇹 Lithuania', region: 'eu' },
    { code: 'LV', label: '🇱🇻 Latvia', region: 'eu' },
    { code: 'EE', label: '🇪🇪 Estonia', region: 'eu' },
    { code: 'RU', label: '🇷🇺 Russia', region: 'eu' },
    { code: 'GE', label: '🇬🇪 Georgia', region: 'eu' },
    { code: 'AM', label: '🇦🇲 Armenia', region: 'eu' },
    { code: 'AZ', label: '🇦🇿 Azerbaijan', region: 'eu' },

    // ═══ AFRICA (54) ═══
    { code: 'MA', label: '🇲🇦 Morocco', region: 'africa' },
    { code: 'DZ', label: '🇩🇿 Algeria', region: 'africa' },
    { code: 'TN', label: '🇹🇳 Tunisia', region: 'africa' },
    { code: 'LY', label: '🇱🇾 Libya', region: 'africa' },
    { code: 'EG', label: '🇪🇬 Egypt', region: 'africa' },
    { code: 'SD', label: '🇸🇩 Sudan', region: 'africa' },
    { code: 'SS', label: '🇸🇸 South Sudan', region: 'africa' },
    { code: 'MR', label: '🇲🇷 Mauritania', region: 'africa' },
    { code: 'ML', label: '🇲🇱 Mali', region: 'africa' },
    { code: 'BF', label: '🇧🇫 Burkina Faso', region: 'africa' },
    { code: 'NE', label: '🇳🇪 Niger', region: 'africa' },
    { code: 'TD', label: '🇹🇩 Chad', region: 'africa' },
    { code: 'SN', label: '🇸🇳 Senegal', region: 'africa' },
    { code: 'GM', label: '🇬🇲 Gambia', region: 'africa' },
    { code: 'GW', label: '🇬🇼 Guinea-Bissau', region: 'africa' },
    { code: 'GN', label: '🇬🇳 Guinea', region: 'africa' },
    { code: 'SL', label: '🇸🇱 Sierra Leone', region: 'africa' },
    { code: 'LR', label: '🇱🇷 Liberia', region: 'africa' },
    { code: 'CI', label: '🇨🇮 Côte d\'Ivoire', region: 'africa' },
    { code: 'GH', label: '🇬🇭 Ghana', region: 'africa' },
    { code: 'TG', label: '🇹🇬 Togo', region: 'africa' },
    { code: 'BJ', label: '🇧🇯 Benin', region: 'africa' },
    { code: 'NG', label: '🇳🇬 Nigeria', region: 'africa' },
    { code: 'CM', label: '🇨🇲 Cameroon', region: 'africa' },
    { code: 'CF', label: '🇨🇫 Central African Rep.', region: 'africa' },
    { code: 'GQ', label: '🇬🇶 Eq. Guinea', region: 'africa' },
    { code: 'GA', label: '🇬🇦 Gabon', region: 'africa' },
    { code: 'CG', label: '🇨🇬 Congo', region: 'africa' },
    { code: 'CD', label: '🇨🇩 DR Congo', region: 'africa' },
    { code: 'AO', label: '🇦🇴 Angola', region: 'africa' },
    { code: 'NA', label: '🇳🇦 Namibia', region: 'africa' },
    { code: 'ZA', label: '🇿🇦 South Africa', region: 'africa' },
    { code: 'BW', label: '🇧🇼 Botswana', region: 'africa' },
    { code: 'ZW', label: '🇿🇼 Zimbabwe', region: 'africa' },
    { code: 'ZM', label: '🇿🇲 Zambia', region: 'africa' },
    { code: 'MW', label: '🇲🇼 Malawi', region: 'africa' },
    { code: 'MZ', label: '🇲🇿 Mozambique', region: 'africa' },
    { code: 'MG', label: '🇲🇬 Madagascar', region: 'africa' },
    { code: 'MU', label: '🇲🇺 Mauritius', region: 'africa' },
    { code: 'KM', label: '🇰🇲 Comoros', region: 'africa' },
    { code: 'SC', label: '🇸🇨 Seychelles', region: 'africa' },
    { code: 'TZ', label: '🇹🇿 Tanzania', region: 'africa' },
    { code: 'KE', label: '🇰🇪 Kenya', region: 'africa' },
    { code: 'UG', label: '🇺🇬 Uganda', region: 'africa' },
    { code: 'RW', label: '🇷🇼 Rwanda', region: 'africa' },
    { code: 'BI', label: '🇧🇮 Burundi', region: 'africa' },
    { code: 'ET', label: '🇪🇹 Ethiopia', region: 'africa' },
    { code: 'ER', label: '🇪🇷 Eritrea', region: 'africa' },
    { code: 'DJ', label: '🇩🇯 Djibouti', region: 'africa' },
    { code: 'SO', label: '🇸🇴 Somalia', region: 'africa' },
    { code: 'CV', label: '🇨🇻 Cabo Verde', region: 'africa' },
    { code: 'ST', label: '🇸🇹 São Tomé', region: 'africa' },
    { code: 'SZ', label: '🇸🇿 Eswatini', region: 'africa' },
    { code: 'LS', label: '🇱🇸 Lesotho', region: 'africa' },

    // ═══ ASIA — Middle East, Central/South/East/SE Asia & Oceania (57) ═══
    // Middle East
    { code: 'SA', label: '🇸🇦 Saudi Arabia', region: 'asia' },
    { code: 'AE', label: '🇦🇪 UAE', region: 'asia' },
    { code: 'QA', label: '🇶🇦 Qatar', region: 'asia' },
    { code: 'KW', label: '🇰🇼 Kuwait', region: 'asia' },
    { code: 'BH', label: '🇧🇭 Bahrain', region: 'asia' },
    { code: 'OM', label: '🇴🇲 Oman', region: 'asia' },
    { code: 'YE', label: '🇾🇪 Yemen', region: 'asia' },
    { code: 'IQ', label: '🇮🇶 Iraq', region: 'asia' },
    { code: 'SY', label: '🇸🇾 Syria', region: 'asia' },
    { code: 'JO', label: '🇯🇴 Jordan', region: 'asia' },
    { code: 'LB', label: '🇱🇧 Lebanon', region: 'asia' },
    { code: 'IL', label: '🇮🇱 Israel', region: 'asia' },
    { code: 'PS', label: '🇵🇸 Palestine', region: 'asia' },
    // Central & South Asia
    { code: 'IR', label: '🇮🇷 Iran', region: 'asia' },
    { code: 'AF', label: '🇦🇫 Afghanistan', region: 'asia' },
    { code: 'PK', label: '🇵🇰 Pakistan', region: 'asia' },
    { code: 'IN', label: '🇮🇳 India', region: 'asia' },
    { code: 'LK', label: '🇱🇰 Sri Lanka', region: 'asia' },
    { code: 'MV', label: '🇲🇻 Maldives', region: 'asia' },
    { code: 'NP', label: '🇳🇵 Nepal', region: 'asia' },
    { code: 'BT', label: '🇧🇹 Bhutan', region: 'asia' },
    { code: 'BD', label: '🇧🇩 Bangladesh', region: 'asia' },
    { code: 'MM', label: '🇲🇲 Myanmar', region: 'asia' },
    { code: 'KZ', label: '🇰🇿 Kazakhstan', region: 'asia' },
    { code: 'UZ', label: '🇺🇿 Uzbekistan', region: 'asia' },
    { code: 'TM', label: '🇹🇲 Turkmenistan', region: 'asia' },
    { code: 'TJ', label: '🇹🇯 Tajikistan', region: 'asia' },
    { code: 'KG', label: '🇰🇬 Kyrgyzstan', region: 'asia' },
    // East & Southeast Asia
    { code: 'CN', label: '🇨🇳 China', region: 'asia' },
    { code: 'JP', label: '🇯🇵 Japan', region: 'asia' },
    { code: 'KR', label: '🇰🇷 South Korea', region: 'asia' },
    { code: 'KP', label: '🇰🇵 North Korea', region: 'asia' },
    { code: 'MN', label: '🇲🇳 Mongolia', region: 'asia' },
    { code: 'TH', label: '🇹🇭 Thailand', region: 'asia' },
    { code: 'VN', label: '🇻🇳 Vietnam', region: 'asia' },
    { code: 'LA', label: '🇱🇦 Laos', region: 'asia' },
    { code: 'KH', label: '🇰🇭 Cambodia', region: 'asia' },
    { code: 'MY', label: '🇲🇾 Malaysia', region: 'asia' },
    { code: 'SG', label: '🇸🇬 Singapore', region: 'asia' },
    { code: 'ID', label: '🇮🇩 Indonesia', region: 'asia' },
    { code: 'PH', label: '🇵🇭 Philippines', region: 'asia' },
    { code: 'BN', label: '🇧🇳 Brunei', region: 'asia' },
    { code: 'TL', label: '🇹🇱 Timor-Leste', region: 'asia' },
    // Oceania
    { code: 'AU', label: '🇦🇺 Australia', region: 'asia' },
    { code: 'NZ', label: '🇳🇿 New Zealand', region: 'asia' },
    { code: 'PG', label: '🇵🇬 Papua New Guinea', region: 'asia' },
    { code: 'FJ', label: '🇫🇯 Fiji', region: 'asia' },
    { code: 'SB', label: '🇸🇧 Solomon Islands', region: 'asia' },
    { code: 'VU', label: '🇻🇺 Vanuatu', region: 'asia' },
    { code: 'WS', label: '🇼🇸 Samoa', region: 'asia' },
    { code: 'TO', label: '🇹🇴 Tonga', region: 'asia' },
    { code: 'KI', label: '🇰🇮 Kiribati', region: 'asia' },
    { code: 'FM', label: '🇫🇲 Micronesia', region: 'asia' },
    { code: 'MH', label: '🇲🇭 Marshall Islands', region: 'asia' },
    { code: 'PW', label: '🇵🇼 Palau', region: 'asia' },
    { code: 'NR', label: '🇳🇷 Nauru', region: 'asia' },
    { code: 'TV', label: '🇹🇻 Tuvalu', region: 'asia' },
];

/** Build sender email: name@lawhelper.{region} */
function getRegionEmail(countryCode: string, senderName?: string): string {
    const c = COUNTRIES.find((x) => x.code === countryCode);
    const region = c ? c.region : 'eu';
    const domain = REGION_DOMAINS[region];
    const defaultName = REGION_DEFAULT_NAMES[region];
    const prefix = senderName ? senderName.toLowerCase().replace(/\s+/g, '.') : defaultName;
    return `${prefix}@${domain}`;
}

/** Group countries by region for display */
const REGIONS_ORDER: EmailRegion[] = ['us', 'lat', 'eu', 'africa', 'asia'];


const PLACEHOLDER_HINT = `Available placeholders:
{{name}}        — Contact's name
{{company}}     — Company name
{{country}}     — Country
{{senderName}}  — Your sender name
{{senderEmail}} — Your sender email`;

// ────────────────────────────────────────────────────────────
// Create Campaign Panel
// ────────────────────────────────────────────────────────────

// ── Available return fields (mirrors backend AVAILABLE_FIELDS) ──
const RETURN_FIELDS_CATALOG: { key: string; label: string; group: 'contact' | 'resource' }[] = [
    // Contact fields
    { key: 'name',        label: '👤 Name',         group: 'contact' },
    { key: 'title',       label: '💼 Title / Role',  group: 'contact' },
    { key: 'companyName', label: '🏢 Company',       group: 'contact' },
    { key: 'email',       label: '✉️ Email',         group: 'contact' },
    { key: 'phone',       label: '📱 Phone',         group: 'contact' },
    { key: 'website',     label: '🌐 Website',       group: 'contact' },
    { key: 'linkedinUrl', label: '🔗 LinkedIn URL',  group: 'contact' },
    { key: 'address',     label: '📍 Address',       group: 'contact' },
    { key: 'specialty',   label: '⚖️ Specialty',     group: 'contact' },
    { key: 'sourceUrl',   label: '📎 Source URL',    group: 'contact' },
    // Resource fields
    { key: 'resourceName', label: '📦 Resource Name', group: 'resource' },
    { key: 'resourceType', label: '🏷️ Resource Type', group: 'resource' },
    { key: 'apiEndpoint',  label: '🔌 API Endpoint',  group: 'resource' },
    { key: 'dataFormat',   label: '📄 Data Format',   group: 'resource' },
    { key: 'accessType',   label: '🔓 Access Type',   group: 'resource' },
    { key: 'provider',     label: '🏛️ Provider',      group: 'resource' },
    { key: 'coverage',     label: '🌍 Coverage',      group: 'resource' },
    { key: 'description',  label: '📝 Description',   group: 'resource' },
];

const DEFAULT_CONTACT_FIELDS = ['name', 'title', 'companyName', 'email', 'specialty', 'sourceUrl'];
const DEFAULT_RESOURCE_FIELDS = ['resourceName', 'resourceType', 'apiEndpoint', 'website', 'accessType', 'provider', 'coverage', 'description', 'sourceUrl'];

type SearchMode = 'contact_discovery' | 'resource_discovery';

function CreateCampaignPanel({ onCreated }: { onCreated: () => void }) {
    const [name, setName] = useState('');
    const [countries, setCountries] = useState<string[]>([]);
    const [audience, setAudience] = useState<TargetAudience>({
        industry: '',
        targetRole: '',
        qualifiers: '',
        exclusions: 'assistants, interns, junior staff',
        targetQuantity: 15,
    });
    const [msg, setMsg] = useState<OutreachMessage>({
        subject: 'Quick opportunity for {{name}} at {{company}}',
        bodyTemplate: `Hi {{name}},

I came across {{company}} and wanted to reach out directly.

[Your value proposition here]

Would you be open to a quick call this week?

Best,
{{senderName}}
{{senderEmail}}`,
    });
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState<Record<string, OutreachMessage>>({});

    // Read auth to check if user is global admin
    const authRaw = localStorage.getItem('acq_auth');
    const auth = authRaw ? JSON.parse(authRaw) : null;
    const isGlobal = auth?.role === 'admin-global';
    
    // Default assigned region
    const defaultRegion = isGlobal ? 'us' : (auth?.regions?.[0] || 'lat');
    const [campaignRegion, setCampaignRegion] = useState<string>(defaultRegion);

    // ── Search mode + dynamic fields ──
    const [searchMode, setSearchMode] = useState<SearchMode>('contact_discovery');
    const [selectedFields, setSelectedFields] = useState<string[]>(DEFAULT_CONTACT_FIELDS);
    const [showFieldPicker, setShowFieldPicker] = useState(false);

    // ── Other fields ──
    const [senderName, setSenderName] = useState('');
    const [campaignGoal, setCampaignGoal] = useState('');
    const [customerDescription, setCustomerDescription] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');
    const [minLeads, setMinLeads] = useState(20);
    const [maxAttempts, setMaxAttempts] = useState(10);
    const [timeoutMinutes, setTimeoutMinutes] = useState(10);

    // When searchMode changes, reset to defaults
    const handleModeChange = (mode: SearchMode) => {
        setSearchMode(mode);
        setSelectedFields(mode === 'resource_discovery' ? DEFAULT_RESOURCE_FIELDS : DEFAULT_CONTACT_FIELDS);
    };

    const toggleField = (key: string) => {
        setSelectedFields(prev =>
            prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key]
        );
    };

    useEffect(() => {
        api<{ templates: Record<string, OutreachMessage> }>('GET', '/templates')
            .then((d) => setTemplates(d.templates))
            .catch(() => { });
    }, []);

    const toggleCountry = (code: string) =>
        setCountries((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);

    async function handleCreate() {
        if (!name || !countries.length || !audience.industry) return;
        setLoading(true);
        try {
            const enrichedAudience = {
                ...audience,
                searchGoal: campaignGoal || undefined,
                idealProfile: customerDescription || undefined,
            };
            await api('POST', '/campaigns', {
                name,
                region: isGlobal ? campaignRegion : undefined,
                senderName,
                targetCountries: countries,
                targetAudience: enrichedAudience,
                outreachMessage: searchMode === 'contact_discovery' ? msg : undefined,
                searchMode,
                returnFields: selectedFields,
                discoveryConfig: { minLeads, maxAttempts, timeoutMs: timeoutMinutes * 60000 },
            });
            onCreated();
            setName('');
            setCountries([]);
            setCampaignGoal('');
            setCustomerDescription('');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ background: '#0f172a', borderRadius: 16, padding: 24, border: '1px solid #1e293b' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700 }}>🎯 New Campaign</h2>

            {/* ── Search Mode Toggle ── */}
            <p style={sectionTitle}>🔀 Search Mode</p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button
                    onClick={() => handleModeChange('contact_discovery')}
                    style={{
                        flex: 1, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                        border: searchMode === 'contact_discovery' ? '2px solid #6366f1' : '1px solid #334155',
                        background: searchMode === 'contact_discovery' ? '#312e8122' : '#0a0f1e',
                        color: searchMode === 'contact_discovery' ? '#a5b4fc' : '#64748b',
                        fontWeight: 700, fontSize: 12, textAlign: 'left' as const,
                    }}
                >
                    <div style={{ fontSize: 18, marginBottom: 4 }}>👤</div>
                    <div>Contact Discovery</div>
                    <div style={{ fontSize: 10, fontWeight: 400, marginTop: 2 }}>Find people, companies, emails, phones</div>
                </button>
                <button
                    onClick={() => handleModeChange('resource_discovery')}
                    style={{
                        flex: 1, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                        border: searchMode === 'resource_discovery' ? '2px solid #22c55e' : '1px solid #334155',
                        background: searchMode === 'resource_discovery' ? '#14532d22' : '#0a0f1e',
                        color: searchMode === 'resource_discovery' ? '#86efac' : '#64748b',
                        fontWeight: 700, fontSize: 12, textAlign: 'left' as const,
                    }}
                >
                    <div style={{ fontSize: 18, marginBottom: 4 }}>🗄️</div>
                    <div>Resource Discovery</div>
                    <div style={{ fontSize: 10, fontWeight: 400, marginTop: 2 }}>Find databases, APIs, datasets, registries</div>
                </button>
            </div>

            {/* ── Return Fields Picker ── */}
            <div style={{ marginBottom: 16 }}>
                <button
                    onClick={() => setShowFieldPicker(!showFieldPicker)}
                    style={{
                        background: 'none', border: 'none', color: '#6366f1',
                        cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        padding: 0, display: 'flex', alignItems: 'center', gap: 4,
                    }}
                >
                    {showFieldPicker ? '▾' : '▸'} 🏷️ Return Fields ({selectedFields.length} selected)
                </button>

                {showFieldPicker && (
                    <div style={{
                        marginTop: 8, padding: 12, background: '#0a0f1e',
                        borderRadius: 10, border: '1px solid #1e293b',
                    }}>
                        <div style={{ display: 'flex', gap: 16 }}>
                            {/* Contact fields */}
                            <div style={{ flex: 1 }}>
                                <p style={{ fontSize: 10, fontWeight: 700, color: '#a5b4fc', marginBottom: 6, textTransform: 'uppercase' }}>Contact Fields</p>
                                {RETURN_FIELDS_CATALOG.filter(f => f.group === 'contact').map(f => (
                                    <label key={f.key} style={{
                                        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
                                        cursor: 'pointer', fontSize: 12,
                                        color: selectedFields.includes(f.key) ? '#e2e8f0' : '#475569',
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedFields.includes(f.key)}
                                            onChange={() => toggleField(f.key)}
                                            style={{ accentColor: '#6366f1' }}
                                        />
                                        {f.label}
                                    </label>
                                ))}
                            </div>
                            {/* Resource fields */}
                            <div style={{ flex: 1 }}>
                                <p style={{ fontSize: 10, fontWeight: 700, color: '#86efac', marginBottom: 6, textTransform: 'uppercase' }}>Resource Fields</p>
                                {RETURN_FIELDS_CATALOG.filter(f => f.group === 'resource').map(f => (
                                    <label key={f.key} style={{
                                        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4,
                                        cursor: 'pointer', fontSize: 12,
                                        color: selectedFields.includes(f.key) ? '#e2e8f0' : '#475569',
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedFields.includes(f.key)}
                                            onChange={() => toggleField(f.key)}
                                            style={{ accentColor: '#22c55e' }}
                                        />
                                        {f.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button onClick={() => setSelectedFields(searchMode === 'resource_discovery' ? DEFAULT_RESOURCE_FIELDS : DEFAULT_CONTACT_FIELDS)}
                                style={{ padding: '4px 10px', background: '#1e293b', color: '#94a3b8', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>
                                Reset to Defaults
                            </button>
                            <button onClick={() => setSelectedFields(RETURN_FIELDS_CATALOG.map(f => f.key))}
                                style={{ padding: '4px 10px', background: '#1e293b', color: '#94a3b8', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>
                                Select All
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {isGlobal && (
                <>
                    <p style={sectionTitle}>🌐 Operating Region</p>
                    <select 
                        style={{ ...inputStyle, marginBottom: 16 }}
                        value={campaignRegion}
                        onChange={(e) => setCampaignRegion(e.target.value)}
                    >
                        <option value="us">North America (lawhelper.us)</option>
                        <option value="lat">Latin America (lawhelper.lat)</option>
                        <option value="eu">Europe (lawhelper.eu)</option>
                        <option value="africa">Africa (lawhelper.africa)</option>
                        <option value="asia">Asia-Pacific (lawhelper.asia)</option>
                    </select>
                </>
            )}

            {/* Sender name + Campaign name */}
            <p style={sectionTitle}>✉️ Sender & Campaign</p>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8, marginBottom: 16 }}>
                <input style={inputStyle}
                    value={senderName} onChange={(e) => setSenderName(e.target.value)}
                    placeholder="Auto by Region" />
                <input style={inputStyle}
                    value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Campaign name, e.g. US SaaS CEOs Q2 2026" />
            </div>
            <p style={{ fontSize: 10, color: '#475569', marginTop: -12, marginBottom: 14 }}>
                Emails will be sent from <strong style={{ color: '#a5b4fc' }}>{senderName || '{Auto_Name}'}@lawhelper.&#123;region&#125;</strong>
            </p>

            {/* ── Campaign Goal / Purpose ── */}
            <p style={sectionTitle}>📌 Campaign Purpose</p>
            <textarea style={{ ...inputStyle, marginBottom: 16, resize: 'vertical' }}
                rows={3}
                value={campaignGoal} onChange={(e) => setCampaignGoal(e.target.value)}
                placeholder="Describe the goal of this campaign. Example:&#10;Find design partners for our AI legal tech platform.&#10;Survey hospitals adopting telemedicine in Southeast Asia.&#10;List all fintech startups with Series A+ funding." />

            {/* ── Customer Type Description ── */}
            <p style={sectionTitle}>👤 Ideal Customer Profile</p>
            <textarea style={{ ...inputStyle, marginBottom: 16, resize: 'vertical' }}
                rows={3}
                value={customerDescription} onChange={(e) => setCustomerDescription(e.target.value)}
                placeholder="Describe the type of target you want to find. Example:&#10;Mid-size law firms (20-200 employees) specializing in corporate law.&#10;Public hospitals with 100+ beds and an IT department.&#10;B2B SaaS companies using React or Node.js in their stack." />

            {/* Target audience */}
            <p style={sectionTitle}>🔍 Search Parameters</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <input style={inputStyle} value={audience.industry}
                    onChange={(e) => setAudience({ ...audience, industry: e.target.value })}
                    placeholder="Industry (e.g. law firms, hospitals, SaaS startups, real estate, NGOs)" />
                <input style={inputStyle} value={audience.targetRole || ''}
                    onChange={(e) => setAudience({ ...audience, targetRole: e.target.value })}
                    placeholder="Target role (optional): e.g. CEO, Doctor, Director" />
            </div>
            <input style={{ ...inputStyle, marginBottom: 8 }} value={audience.qualifiers || ''}
                onChange={(e) => setAudience({ ...audience, qualifiers: e.target.value })}
                placeholder="Qualifiers (optional): e.g. 10+ employees, Series A funded" />
            <input style={{ ...inputStyle, marginBottom: 16 }} value={audience.exclusions || ''}
                onChange={(e) => setAudience({ ...audience, exclusions: e.target.value })}
                placeholder="Exclude (optional): e.g. assistants, interns, junior staff" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                <div>
                    <label style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4, display: 'block' }}>🎯 Target contacts per search pass</label>
                    <input type="number" style={inputStyle} value={audience.targetQuantity || 15}
                        onChange={(e) => setAudience({ ...audience, targetQuantity: Math.max(1, parseInt(e.target.value) || 15) })}
                        min={1} max={50} />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <p style={{ fontSize: 10, color: '#475569', margin: 0 }}>
                        Agent will keep searching until it finds this many contacts. Higher = longer search time, more results.
                    </p>
                </div>
            </div>

            {/* Countries — grouped by region */}
            <p style={sectionTitle}>🌍 Target Countries <span style={{ color: '#a5b4fc', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>({countries.length} / {COUNTRIES.length} selected)</span></p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input
                    style={{ ...inputStyle, flex: 1 }}
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    placeholder="Search countries..."
                />
                <button onClick={() => {
                    const filtered = COUNTRIES.filter(c => c.label.toLowerCase().includes(countrySearch.toLowerCase()));
                    setCountries(prev => Array.from(new Set([...prev, ...filtered.map(c => c.code)])));
                }} style={{
                    padding: '6px 12px', background: '#1e3a5f', color: '#7dd3fc',
                    border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                }}>Select All</button>
                <button onClick={() => {
                    if (countrySearch) {
                        const filtered = COUNTRIES.filter(c => c.label.toLowerCase().includes(countrySearch.toLowerCase())).map(c => c.code);
                        setCountries(prev => prev.filter(c => !filtered.includes(c)));
                    } else {
                        setCountries([]);
                    }
                }} style={{
                    padding: '6px 12px', background: '#450a0a', color: '#fca5a5',
                    border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                }}>Deselect All</button>
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto', marginBottom: 16, border: '1px solid #1e293b', borderRadius: 8, padding: '8px' }}>
                {REGIONS_ORDER.map((region) => {
                    const meta = REGION_META[region];
                    const regionCountries = COUNTRIES.filter(c => c.region === region && c.label.toLowerCase().includes(countrySearch.toLowerCase()));
                    if (regionCountries.length === 0) return null;
                    const regionSelected = regionCountries.filter(c => countries.includes(c.code)).length;
                    return (
                        <div key={region} style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, padding: '4px 6px', borderRadius: 6, background: '#0a0f1e' }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: meta.color }}>
                                    {meta.icon} {meta.label}
                                    <span style={{ color: '#475569', fontWeight: 400, marginLeft: 6 }}>{senderName || REGION_DEFAULT_NAMES[region]}@{REGION_DOMAINS[region]}</span>
                                    <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 6 }}>({regionSelected}/{regionCountries.length})</span>
                                </span>
                                <button onClick={() => {
                                    const codes = regionCountries.map(c => c.code);
                                    const allSelected = codes.every(c => countries.includes(c));
                                    if (allSelected) {
                                        setCountries(prev => prev.filter(c => !codes.includes(c)));
                                    } else {
                                        setCountries(prev => Array.from(new Set([...prev, ...codes])));
                                    }
                                }} style={{
                                    padding: '2px 8px', background: 'transparent', color: meta.color,
                                    border: `1px solid ${meta.color}33`, borderRadius: 4, cursor: 'pointer', fontSize: 10, fontWeight: 600,
                                }}>{regionCountries.every(c => countries.includes(c.code)) ? 'Deselect' : 'Select All'}</button>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, paddingLeft: 4 }}>
                                {regionCountries.map((c) => (
                                    <button key={c.code} onClick={() => toggleCountry(c.code)} style={{
                                        padding: '3px 9px', borderRadius: 999, border: '1px solid',
                                        borderColor: countries.includes(c.code) ? meta.color : '#334155',
                                        background: countries.includes(c.code) ? `${meta.color}22` : 'transparent',
                                        color: countries.includes(c.code) ? meta.color : '#94a3b8',
                                        cursor: 'pointer', fontSize: 11, fontWeight: 600,
                                    }}>{c.label}</button>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ── Advanced Discovery Settings ── */}
            <div style={{ marginBottom: 16 }}>
                <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    style={{
                        background: 'none', border: 'none', color: '#6366f1',
                        cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        padding: 0, display: 'flex', alignItems: 'center', gap: 4,
                    }}
                >
                    {showAdvanced ? '▾' : '▸'} ⚙️ Discovery Settings
                </button>

                {showAdvanced && (
                    <div style={{
                        marginTop: 10, padding: 16, background: '#0a0f1e',
                        borderRadius: 10, border: '1px solid #1e293b',
                    }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                            <div>
                                <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                    Min Leads / Country
                                </label>
                                <input type="number" min={5} max={100} style={{ ...inputStyle, textAlign: 'center' as any }}
                                    value={minLeads} onChange={(e) => setMinLeads(Number(e.target.value))} />
                                <span style={{ fontSize: 10, color: '#475569', display: 'block', marginTop: 2 }}>
                                    Stop when reached
                                </span>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                    Max Search Rounds
                                </label>
                                <input type="number" min={1} max={20} style={{ ...inputStyle, textAlign: 'center' as any }}
                                    value={maxAttempts} onChange={(e) => setMaxAttempts(Number(e.target.value))} />
                                <span style={{ fontSize: 10, color: '#475569', display: 'block', marginTop: 2 }}>
                                    Strategy rotation cycles
                                </span>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                    Timeout (minutes)
                                </label>
                                <input type="number" min={1} max={30} style={{ ...inputStyle, textAlign: 'center' as any }}
                                    value={timeoutMinutes} onChange={(e) => setTimeoutMinutes(Number(e.target.value))} />
                                <span style={{ fontSize: 10, color: '#475569', display: 'block', marginTop: 2 }}>
                                    Per search round
                                </span>
                            </div>
                        </div>
                        <p style={{ fontSize: 10, color: '#475569', marginTop: 8, marginBottom: 0 }}>
                            Agent searches via 5 strategies: Google → LinkedIn → Associations → Team pages → Email inference.
                            Each round uses the next strategy in rotation.
                        </p>
                    </div>
                )}
            </div>

            {/* Email template — only for contact_discovery mode */}
            {searchMode === 'contact_discovery' && (
            <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <p style={{ ...sectionTitle, marginBottom: 0 }}>✉️ Email Template</p>
                {Object.keys(templates).length > 0 && (
                    <select
                        onChange={(e) => {
                            const t = templates[e.target.value];
                            if (t) setMsg(t);
                        }}
                        style={{ ...inputStyle, width: 'auto', padding: '4px 8px', fontSize: 11 }}
                    >
                        <option value="">Load a template...</option>
                        {Object.keys(templates).map((k) => (
                            <option key={k} value={k}>{k}</option>
                        ))}
                    </select>
                )}
            </div>
            <input style={{ ...inputStyle, marginBottom: 8 }} value={msg.subject}
                onChange={(e) => setMsg({ ...msg, subject: e.target.value })}
                placeholder="Subject (can include {{name}}, {{company}})" />
            <textarea
                value={msg.bodyTemplate}
                onChange={(e) => setMsg({ ...msg, bodyTemplate: e.target.value })}
                rows={10}
                style={{ ...inputStyle, resize: 'vertical', marginBottom: 6 }}
                placeholder={PLACEHOLDER_HINT}
            />
            <p style={{ fontSize: 10, color: '#475569', marginBottom: 16 }}>
                {PLACEHOLDER_HINT.split('\n').map((l, i) => <span key={i}>{l}<br /></span>)}
            </p>
            </>
            )}

            {/* Resource discovery info banner */}
            {searchMode === 'resource_discovery' && (
                <div style={{
                    padding: 14, marginBottom: 16, borderRadius: 10,
                    background: 'linear-gradient(135deg, #14532d11, #16a34a11)',
                    border: '1px solid #22c55e33',
                }}>
                    <p style={{ margin: 0, fontSize: 12, color: '#86efac', fontWeight: 600 }}>
                        🗄️ Resource Discovery Mode
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: '#64748b', lineHeight: 1.5 }}>
                        AI will search for databases, APIs, datasets, and registries instead of contacts.
                        No emails will be sent — results are saved for your review.
                        The agent searches government portals, GitHub, Hugging Face, and API directories.
                    </p>
                </div>
            )}

            <button onClick={handleCreate}
                disabled={loading || !name || !countries.length || !audience.industry}
                style={{
                    width: '100%', padding: 12,
                    background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                    color: '#fff', border: 'none', borderRadius: 10,
                    cursor: 'pointer', fontWeight: 700, fontSize: 15,
                    opacity: loading || !name || !countries.length || !audience.industry ? 0.5 : 1,
                }}>
                {loading ? 'Creating...' : '+ Create Campaign'}
            </button>
        </div>
    );
}

// ────────────────────────────────────────────────────────────
// Negotiation Chat
// ────────────────────────────────────────────────────────────

function NegotiationChat({ lead, threads, onSent }: {
    lead: Lead; threads: Thread[]; onSent: () => void;
}) {
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const latest = [...threads].reverse().find((t) => t.direction === 'inbound');

    async function send() {
        if (!text.trim()) return;
        setSending(true);
        try {
            await api('POST', '/reply', {
                leadId: lead.id,
                toEmail: lead.email,
                fromEmail: latest?.toEmail || getRegionEmail(lead.country),
                subject: latest ? `Re: ${latest.subject}` : 'Follow-up',
                bodyText: text,
                inReplyToMessageId: latest?.id,
            });
            setText('');
            onSent();
        } catch (e) { alert(String(e)); }
        finally { setSending(false); }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
            <div style={{
                flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column',
                gap: 10, padding: 12, background: '#0a0f1e', borderRadius: 10, border: '1px solid #1e293b',
            }}>
                {threads.length === 0 && (
                    <p style={{ color: '#475569', textAlign: 'center', marginTop: 40, fontSize: 13 }}>
                        No messages yet.
                    </p>
                )}
                {threads.map((t) => (
                    <div key={t.id} style={{
                        alignSelf: t.direction === 'outbound' ? 'flex-end' : 'flex-start',
                        maxWidth: '85%', background: t.direction === 'outbound' ? '#312e81' : '#1e293b',
                        padding: '10px 14px', borderRadius: 10,
                    }}>
                        <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4 }}>
                            {t.direction === 'outbound' ? `You → ${t.toEmail}` : `${t.fromEmail} → You`}
                            {' · '}{new Date(t.receivedAt).toLocaleString()}
                        </div>
                        <div style={{ fontSize: 13, color: '#e2e8f0', whiteSpace: 'pre-wrap' }}>
                            {t.bodyText}
                        </div>
                    </div>
                ))}
            </div>
            <textarea
                value={text} onChange={(e) => setText(e.target.value)}
                rows={4} placeholder="Type your reply..."
                style={{ ...inputStyle, resize: 'vertical' }}
            />
            <button onClick={send} disabled={sending || !text.trim()} style={{
                padding: 10, background: '#22c55e', color: '#fff', border: 'none',
                borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14,
                opacity: sending || !text.trim() ? 0.5 : 1,
            }}>
                {sending ? 'Sending...' : '📤 Send Reply'}
            </button>
        </div>
    );
}

// ────────────────────────────────────────────────────────────
// Main Dashboard
// ────────────────────────────────────────────────────────────

export function CampaignDashboard() {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [tab, setTab] = useState<'campaigns' | 'leads'>('campaigns');
    const [selected, setSelected] = useState<Campaign | null>(null);
    const [leads, setLeads] = useState<Lead[]>([]);
    const [lead, setLead] = useState<Lead | null>(null);
    const [threads, setThreads] = useState<Thread[]>([]);
    const [loading, setLoading] = useState(false);
    const [sendEngine, setSendEngine] = useState<'smart' | 'outlook' | 'spacemail'>('smart');
    const [outlookCapacity, setOutlookCapacity] = useState<{ totalAccounts: number; availableAccounts: number; totalCapacity: number; remainingCapacity: number } | null>(null);

    const loadCampaigns = useCallback(async () => {
        try { setCampaigns((await api<{ campaigns: Campaign[] }>('GET', '/campaigns')).campaigns); }
        catch { }
    }, []);

    useEffect(() => {
        loadCampaigns();
        // Load Outlook pool capacity
        api<{ success: boolean; totalAccounts: number; availableAccounts: number; totalCapacity: number; remainingCapacity: number }>('GET', '/dispatch/capacity')
            .then(d => setOutlookCapacity(d))
            .catch(() => { });
    }, [loadCampaigns]);

    // ── Auto-poll campaigns every 10s when any campaign is running ──
    useEffect(() => {
        const hasRunning = campaigns.some(c => c.status === 'running');
        if (!hasRunning) return;

        const interval = setInterval(() => {
            loadCampaigns();
        }, 10_000); // 10 seconds

        return () => clearInterval(interval);
    }, [campaigns, loadCampaigns]);

    async function selectCampaign(c: Campaign) {
        setSelected(c);
        setTab('leads');
        setLeads((await api<{ leads: Lead[] }>('GET', `/campaigns/${c.id}`)).leads);
    }

    async function discover(id: string) {
        setLoading(true);
        try { await api('POST', `/campaigns/${id}/discover`, {}); await loadCampaigns(); }
        finally { setLoading(false); }
    }

    async function pauseCampaign(id: string) {
        setLoading(true);
        try { await api('PATCH', `/campaigns/${id}/status`, { status: 'paused' }); await loadCampaigns(); }
        finally { setLoading(false); }
    }

    async function resumeCampaign(id: string) {
        setLoading(true);
        try { await api('PATCH', `/campaigns/${id}/status`, { status: 'running' }); await loadCampaigns(); }
        finally { setLoading(false); }
    }

    async function deleteCampaign(id: string, name: string) {
        if (!window.confirm(`Delete "${name}" and all its leads?\n\nThis cannot be undone.`)) return;
        setLoading(true);
        try {
            await api('DELETE', `/campaigns/${id}`);
            if (selected?.id === id) { setSelected(null); setTab('campaigns'); }
            await loadCampaigns();
        } finally { setLoading(false); }
    }

    async function sendApproved() {
        if (!selected) return;
        setLoading(true);
        try { await api('POST', `/campaigns/${selected.id}/send`, { engine: sendEngine }); }
        finally { setLoading(false); }
    }

    async function patchStatus(leadId: string, status: string) {
        await api('PATCH', `/leads/${leadId}/status`, { status });
        setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, status } : l));
    }

    async function selectLead(l: Lead) {
        setLead(l);
        setThreads((await api<{ threads: Thread[] }>('GET', `/leads/${l.id}/threads`)).threads);
    }

    return (
        <div>
            {/* Page Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                    <h2 className="page-title">🎯 Campaigns</h2>
                    <p className="page-subtitle">Create campaigns, discover leads &amp; send outreach</p>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {(['campaigns', 'leads'] as const).map((t) => (
                    <button key={t} onClick={() => setTab(t)}
                        className={`filter-chip${tab === t ? ' active' : ''}`}>
                        {t === 'leads' && selected ? `Leads — ${selected.name}` : t}
                    </button>
                ))}
            </div>

            {/* ── Campaigns Tab ── */}
            {tab === 'campaigns' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
                    <CreateCampaignPanel onCreated={loadCampaigns} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {campaigns.length === 0 && (
                            <p style={{ color: '#475569', textAlign: 'center', paddingTop: 60, fontSize: 14 }}>
                                No campaigns yet.
                            </p>
                        )}
                        {campaigns.map((c) => (
                            <div key={c.id} onClick={() => selectCampaign(c)}
                                style={{
                                    background: '#0f172a', border: '1px solid #1e293b', borderRadius: 14,
                                    padding: '16px 20px', cursor: 'pointer', transition: 'border-color 0.15s',
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#7c3aed')}
                                onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#1e293b')}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: 15 }}>
                                            {c.name}
                                            {c.region && (
                                                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '2px 6px', background: '#1e293b', borderRadius: 4, color: '#94a3b8', border: '1px solid #334155' }}>
                                                    {c.region.toUpperCase()}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                                            {c.targetAudience.targetRole} @ {c.targetAudience.industry}
                                        </div>
                                        <div style={{ color: '#475569', fontSize: 11 }}>
                                            {c.targetCountries.join(', ')}
                                        </div>
                                    </div>
                                    <Badge label={c.status} />
                                </div>
                                <div style={{ display: 'flex', gap: 20, marginTop: 12 }}>
                                    {[
                                        { l: 'Leads', v: c.leadsDiscovered },
                                        { l: 'Sent', v: c.emailsSent },
                                        { l: 'Replies', v: c.repliesReceived },
                                        { l: 'Converted', v: c.converted },
                                    ].map((s) => (
                                        <div key={s.l}>
                                            <div style={{ fontSize: 20, fontWeight: 800, color: '#a5b4fc' }}>{s.v}</div>
                                            <div style={{ fontSize: 11, color: '#64748b' }}>{s.l}</div>
                                        </div>
                                    ))}
                                </div>
                                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                                    {c.status !== 'running' && (
                                        <button onClick={(e) => { e.stopPropagation(); discover(c.id); }}
                                            disabled={loading}
                                            style={{
                                                padding: '6px 14px', background: '#1e3a5f', color: '#7dd3fc',
                                                border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                            }}>
                                            🔍 Discover Leads
                                        </button>
                                    )}
                                    {c.status === 'paused' && (
                                        <button onClick={(e) => { e.stopPropagation(); resumeCampaign(c.id); }}
                                            disabled={loading}
                                            style={{
                                                padding: '6px 14px', background: '#166534', color: '#86efac',
                                                border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                            }}>
                                            ▶ Resume
                                        </button>
                                    )}
                                    {c.status === 'running' && (
                                        <button onClick={(e) => { e.stopPropagation(); pauseCampaign(c.id); }}
                                            disabled={loading}
                                            style={{
                                                padding: '6px 14px', background: '#78350f', color: '#fbbf24',
                                                border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                            }}>
                                            ⏸ Pause
                                        </button>
                                    )}
                                    {c.status !== 'running' && (
                                        <button onClick={(e) => { e.stopPropagation(); deleteCampaign(c.id, c.name); }}
                                            disabled={loading}
                                            style={{
                                                padding: '6px 14px', background: '#450a0a', color: '#fca5a5',
                                                border: '1px solid #7f1d1d', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                                marginLeft: 'auto',
                                            }}>
                                            🗑 Delete
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Leads Tab ── */}
            {tab === 'leads' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 20 }}>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                            <span style={{ fontWeight: 700, fontSize: 15 }}>
                                {leads.length} leads
                            </span>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                {/* Engine Selector */}
                                <div style={{ display: 'flex', gap: 4, background: '#0a0f1e', borderRadius: 8, padding: 3, border: '1px solid #1e293b' }}>
                                    {[
                                        { key: 'smart' as const, label: '🎯 Smart', tip: 'Auto-route: Outlook→Outlook, Spacemail→others' },
                                        { key: 'outlook' as const, label: '📧 Outlook Pool', tip: `${outlookCapacity?.remainingCapacity || 0} sends left today` },
                                        { key: 'spacemail' as const, label: '✉️ Spacemail', tip: 'lawhelper.* domains' },
                                    ].map(eng => (
                                        <button key={eng.key}
                                            onClick={() => setSendEngine(eng.key)}
                                            title={eng.tip}
                                            style={{
                                                padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                                fontSize: 11, fontWeight: 600, transition: 'all 0.15s',
                                                background: sendEngine === eng.key ? '#312e81' : 'transparent',
                                                color: sendEngine === eng.key ? '#a5b4fc' : '#64748b',
                                            }}
                                        >{eng.label}</button>
                                    ))}
                                </div>
                                {outlookCapacity && sendEngine !== 'spacemail' && (
                                    <span style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap' }}>
                                        Outlook: {outlookCapacity.remainingCapacity}/{outlookCapacity.totalCapacity} left
                                    </span>
                                )}
                                <button onClick={sendApproved} disabled={loading} style={{
                                    padding: '8px 18px', background: '#22c55e', color: '#fff',
                                    border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13,
                                }}>
                                    📬 Send All Approved
                                </button>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {leads.map((l) => (
                                <div key={l.id} onClick={() => selectLead(l)}
                                    style={{
                                        background: lead?.id === l.id ? '#1e1b4b' : '#0f172a',
                                        border: `1px solid ${lead?.id === l.id ? '#7c3aed' : '#1e293b'}`,
                                        borderRadius: 12, padding: '12px 16px', cursor: 'pointer',
                                        display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center',
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: 14 }}>
                                            {l.name}
                                            <span style={{ color: '#64748b', fontWeight: 400, fontSize: 13 }}>
                                                {' '}— {l.title}
                                            </span>
                                        </div>
                                        <div style={{ color: '#94a3b8', fontSize: 13 }}>{l.companyName} · {l.country}</div>
                                        <a href={`mailto:${l.email}`} onClick={(e) => e.stopPropagation()}
                                            style={{ color: '#6366f1', fontSize: 12 }}>{l.email}</a>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                                        <Badge label={l.status} />
                                        {l.status === 'discovered' && (
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <button onClick={(e) => { e.stopPropagation(); patchStatus(l.id, 'approved'); }}
                                                    style={{ padding: '3px 10px', background: '#166534', color: '#86efac', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                                                    ✓ Approve
                                                </button>
                                                <button onClick={(e) => { e.stopPropagation(); patchStatus(l.id, 'rejected'); }}
                                                    style={{ padding: '3px 10px', background: '#450a0a', color: '#fca5a5', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                                                    ✕ Reject
                                                </button>
                                            </div>
                                        )}
                                        {l.status === 'replied' && (
                                            <span style={{ color: '#f59e0b', fontSize: 11, fontWeight: 700 }}>💬 Has reply</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {leads.length === 0 && (
                                <p style={{ color: '#475569', textAlign: 'center', paddingTop: 60, fontSize: 14 }}>
                                    No leads yet. Click "Discover Leads" on a campaign.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Negotiation Chat */}
                    {lead && (
                        <div style={{
                            background: '#0f172a', border: '1px solid #1e293b', borderRadius: 16,
                            padding: 20, display: 'flex', flexDirection: 'column', height: 600,
                        }}>
                            <div style={{ marginBottom: 12 }}>
                                <div style={{ fontWeight: 700, fontSize: 15 }}>{lead.name}</div>
                                <div style={{ color: '#94a3b8', fontSize: 12 }}>{lead.title} @ {lead.companyName}</div>
                                <div style={{ color: '#6366f1', fontSize: 12 }}>{lead.email}</div>
                            </div>
                            <NegotiationChat
                                lead={lead}
                                threads={threads}
                                onSent={async () => {
                                    setThreads((await api<{ threads: Thread[] }>('GET', `/leads/${lead.id}/threads`)).threads);
                                }}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
